<template>
    <section>
            <router-view name="header"></router-view>
            <router-view name="main" :key="mainViewKey"></router-view>
            <router-view name="footer"></router-view>
            <div
                v-if="showOfflineNotice"
                class="offline-screen"
                role="status"
                aria-live="assertive"
            >
                <div class="offline-card">
                    <img :src="offlineIcon" alt="" class="offline-icon" />
                    <h1>Keine Verbindung</h1>
                    <p>
                        Bitte WLAN oder Mobilfunk prüfen. Die App macht weiter,
                        sobald das Netz wieder da ist.
                    </p>
                </div>
            </div>
            <div
                v-if="appBlock"
                class="app-block-screen"
                role="alertdialog"
                aria-modal="true"
                aria-labelledby="app-block-title"
            >
                <div class="app-block-card">
                    <h1 id="app-block-title">App-Update nötig</h1>
                    <p class="app-block-message">
                        {{ appBlock.message || defaultBlockMessage }}
                    </p>
                    <button
                        v-if="appBlock.storeUrl"
                        type="button"
                        class="app-block-btn app-block-btn-primary"
                        @click="openStore"
                    >
                        App aktualisieren
                    </button>
                    <button
                        v-if="appBlock.webAppUrl"
                        type="button"
                        class="app-block-btn"
                        @click="openWebApp"
                    >
                        Im Browser weiterarbeiten
                    </button>
                </div>
            </div>
    </section>
</template>

<script>
import { sendUsageHeartbeat } from '@/services/usage.service';
import {
    startCompatibilityWatch,
    openExternalUrl,
} from '@/services/appCompatibility';
import { isMobileBuild, isNativeApp } from '@/utils/platform';
import { lockAppPageZoom } from '@/utils/nativeShell';
import { subscribeNetworkStatus } from '@/utils/networkStatus';
import offlineIcon from '@/assets/no-network.png';

export default {
    data() {
        return {
            isOnline: true,
            offlineIcon,
            appBlock: null,
            defaultBlockMessage:
                'Diese App-Version wird nicht mehr unterstützt. Bitte aktualisiere die App oder arbeite im Browser weiter. Die Web-App kann die Foto-Reihenfolge nicht garantieren.',
        }
    },
    computed: {
        // Routen mit meta.keepOnQueryChange schreiben ihren Zustand (z. B. Suche, Seite)
        // in die Query – das darf die View nicht neu mounten.
        mainViewKey() {
            return this.$route.meta?.keepOnQueryChange
                ? this.$route.path
                : this.$route.fullPath;
        },
        showOfflineNotice() {
            return isMobileBuild() && !this.isOnline;
        },
    },
    mounted() {
        if (isMobileBuild()) {
            document.documentElement.classList.add('mobile-shell');
            lockAppPageZoom();
        }
        this.unsubscribeNetwork = subscribeNetworkStatus(this.handleConnectionChange);
        sendUsageHeartbeat();
        if (isNativeApp()) {
            startCompatibilityWatch({
                onBlock: (info) => {
                    this.appBlock = info;
                },
                onClear: () => {
                    this.appBlock = null;
                },
            });
        }
    },
    beforeUnmount() {
        document.documentElement.classList.remove('mobile-shell');
        if (this.unsubscribeNetwork) {
            this.unsubscribeNetwork();
        }
    },
    methods: {
        handleConnectionChange(connected) {
            this.isOnline = connected;
            if (!this.isOnline && !isMobileBuild()) {
                this.$toast.info('Sie sind derzeit offline. Bitte prüfen Sie Ihre Internetverbindung.');
            }
        },
        openStore() {
            openExternalUrl(this.appBlock && this.appBlock.storeUrl);
        },
        openWebApp() {
            openExternalUrl(this.appBlock && this.appBlock.webAppUrl);
        },
    }
};
</script>


<style>
:root {
    --app-header-height: 3.25rem;
    --app-footer-height: 2.25rem;
}

html.mobile-shell {
    --app-header-height: calc(3.25rem + env(safe-area-inset-top, 0px));
    --app-footer-height: calc(3.7rem + env(safe-area-inset-bottom, 0px));
}

html.mobile-shell,
html.mobile-shell body {
    background-color: #fff;
    touch-action: manipulation;
}

html.mobile-shell .Vue-Toastification__container.top-left,
html.mobile-shell .Vue-Toastification__container.top-right,
html.mobile-shell .Vue-Toastification__container.top-center {
    top: calc(var(--app-header-height) + 0.35rem);
}

html.mobile-shell .Vue-Toastification__container.bottom-left,
html.mobile-shell .Vue-Toastification__container.bottom-right,
html.mobile-shell .Vue-Toastification__container.bottom-center {
    bottom: calc(var(--app-footer-height) + 0.35rem);
}

* {
    margin: 0px;
    padding: 0px;
    box-sizing: border-box;
}

.btn {
    padding: 0.4rem 0.85rem;
    font-size: 0.95rem;
}

.btn-lg {
    padding: 0.5rem 1rem;
    font-size: 1rem;
}

.error-message-screen,
h1 {
    width: 80vw;
    height: 60vh;
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 15vh;
    margin-left: 10vw;
    flex-direction: column;
}

img {
    width: 250px;
    height: 250px;
}

.app-block-screen {
    position: fixed;
    z-index: 10000;
    inset: 0;
    background: rgba(13, 27, 48, 0.92);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
}

.app-block-card {
    width: min(26rem, 100%);
    background: #1c335a;
    color: #fff;
    border-radius: 16px;
    padding: 1.5rem 1.35rem 1.25rem;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
    display: flex;
    flex-direction: column;
    gap: 0.85rem;
}

.app-block-card h1 {
    width: auto;
    height: auto;
    margin: 0;
    display: block;
    font-size: 1.35rem;
    line-height: 1.3;
    text-align: center;
}

.app-block-message {
    margin: 0;
    font-size: 0.95rem;
    line-height: 1.45;
    color: #d5dbe8;
    text-align: center;
}

.app-block-btn {
    width: 100%;
    border: 2px solid #fff;
    border-radius: 10px;
    background: transparent;
    color: #fff;
    padding: 0.7rem 1rem;
    font-size: 1rem;
    cursor: pointer;
}

.app-block-btn-primary {
    background: #fff;
    color: #1c335a;
}

.offline-screen {
    position: fixed;
    z-index: 9000;
    inset: 0;
    background: rgba(13, 27, 48, 0.92);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1.5rem;
}

.offline-card {
    width: min(24rem, 100%);
    background: #1c335a;
    color: #fff;
    border-radius: 16px;
    padding: 1.5rem 1.35rem 1.25rem;
    box-shadow: 0 8px 32px rgba(0, 0, 0, 0.35);
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.7rem;
    text-align: center;
}

.offline-icon {
    width: 5.5rem;
    height: 5.5rem;
    object-fit: contain;
}

.offline-card h1 {
    width: auto;
    height: auto;
    margin: 0;
    display: block;
    font-size: 1.35rem;
    line-height: 1.3;
}

.offline-card p {
    margin: 0;
    font-size: 0.95rem;
    line-height: 1.45;
    color: #d5dbe8;
}

/* 
h1{
    display: flex;
    justify-content: center;
    align-items: center;
    
} */
</style>




















<!-- <template>
    <section>
<router-view name="header" id="header"></router-view>
<router-view name="main" id="main"></router-view>
<router-view name="footer" id="footer"></router-view>
    </section>
</template>

<script>


export default {
    created() {
if (localStorage.getItem('token') === null)
{
    this.$router.push('/login');
}
    },
    components: {

    },
    data() {
return {
    isLoggedIn: true,
    post: null,
};
    },
};
</script>

<style>
* {
    margin: 0px;
    padding: 0px;
    box-sizing: border-box;
}
</style> -->

<!-- // import Login from './components/login/LoginForm.vue';
// import MainView from './components/home/MainView.vue';
// Login,
// MainView, -->